import { Injectable } from '@angular/core';
import { IMediaBankFilesCreationDateCheckData } from '@fi-shared/media-bank/media-bank';
import { FileType } from '@fi-shared/types';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
const MP4Box = require('mp4box');

interface IExifData {
    DateTime?: Date;
}

interface IExifResult {
    fileName?: string;
    exifData?: IExifData;
}

@Injectable()
export class FiExifHelperService {
    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private mediaStorageService: FiMediaStorageService,
        private fiLoading: LoadingService,
    ) {}

    checkFileImagesCreationDate(files: ArrayLike<File>, deltaMs: number, skipErrorDialog?: boolean): Promise<boolean> {
        const exifPromises: Promise<IExifResult>[] = [];
        Array.from(files).forEach((file) => {
            if (this.mediaStorageService.isFileWithMetadata(file)) {
                const processPromise = this.getExifDataFromFile(file);
                exifPromises.push(processPromise);
            }
        });
        return this.fiLoading.trackPromise(this.checkCreationDateInternal(exifPromises, deltaMs, skipErrorDialog));
    }

    getExifDataInternal(data: File): Promise<IExifData> {
        const promise = new Promise<IExifData>((resolve, reject) => {
            const exifData: IExifData = {};
            try {
                if (this.mediaStorageService.isImageWithMetadata(data)) {
                    const exifr = window['exifr'];
                    exifr
                        .parse(data)
                        .then((result) => {
                            if (result?.DateTimeOriginal) {
                                exifData.DateTime = result.DateTimeOriginal;
                            }
                        })
                        .finally(() => {
                            resolve(exifData);
                        });
                } else if (this.mediaStorageService.isVideoWithMetadata(data)) {
                    this.loadVideoFileUpload(data as File)
                        .then((info) => {
                            if (info.created) {
                                exifData.DateTime = info.created;
                            }
                        })
                        .finally(() => resolve(exifData));
                } else {
                    console.log('getExifDataInternal not implemented', data);
                    resolve(exifData);
                }
                /*const fileReader = new FileReader();
                fileReader.onload = ((result) => {
                    const arrayBuffer: any = result.target.result;
                    arrayBuffer.fileStart = 0;
                    const mp4boxfile = MP4Box.createFile();
                    mp4boxfile.onReady = (info) => {
                        if (info.created) {
                            exifData.DateTime = info.created;
                        }
                        resolve(exifData);
                    };
                    mp4boxfile.onError = (e) => {
                        resolve(exifData);
                    };
                    mp4boxfile.appendBuffer(arrayBuffer);
                    mp4boxfile.flush();
                });
                fileReader.onerror = () => {
                    resolve(exifData);
                };
                fileReader.readAsArrayBuffer(<any>data);*/
            } catch (ex) {
                resolve(exifData);
            }
        });
        promise.then((exifData) => {});
        return promise;
    }

    private loadVideoFileUpload(file: File): Promise<{ created: Date }> {
        const promise = new Promise<{ created: Date }>((resolve, reject) => {
            if (!file.type.startsWith('video/')) {
                reject();
                return;
            }

            let resolved = false;
            const fileSize = file.size;
            let offset = 0;
            let readBlock = null;
            let chunkSize = 1048576;

            const mp4boxfile = MP4Box.createFile(false);
            mp4boxfile.onReady = (info) => {
                resolved = true;
                resolve(info);
            };
            mp4boxfile.onError = (e) => {
                reject();
            };

            const onparsedbuffer = function (mp4boxfile, buffer) {
                buffer.fileStart = offset;
                mp4boxfile.appendBuffer(buffer);
            };

            const onBlockRead = function (evt) {
                if (resolved) {
                    return;
                }
                if (evt.target.error == null) {
                    onparsedbuffer(mp4boxfile, evt.target.result); // callback for handling read chunk
                    offset += evt.target.result.byteLength;
                } else {
                    reject();
                    return;
                }
                if (offset >= fileSize) {
                    return;
                }

                readBlock(offset, chunkSize, file);
            };

            readBlock = function (_offset, length, _file) {
                const r = new FileReader();
                const blob = _file.slice(_offset, length + _offset);
                r.onload = onBlockRead;
                r.readAsArrayBuffer(blob);
            };

            readBlock(offset, chunkSize, file);
        });
        return promise;
    }

    getExifDataFromFile(file: File) {
        const result: IExifResult = {
            fileName: file.name,
        };
        return new Promise<IExifResult>((resolve, reject) => {
            try {
                this.getExifDataInternal(file).then(
                    (exifData) => {
                        result.exifData = exifData;
                        resolve(result);
                    },
                    () => {
                        resolve(result);
                    },
                );
            } catch (e) {
                resolve(result);
            }
        });
    }

    checkMediaBankFilesCreationDate<T extends IMediaBankFilesCreationDateCheckData>(
        mediaItems: T[],
        deltaMs: number,
        skipErrorDialog?: boolean,
    ): Promise<boolean> {
        const exifPromises: Promise<IExifResult>[] = [];
        mediaItems.forEach((mediaItem) => {
            if (
                mediaItem.FileType === FileType.Image ||
                mediaItem.FileType === FileType.Movie ||
                mediaItem.FileType === FileType.MovieNotPlayable
            ) {
                // const processPromise = this.getExifDataFromUrlViaBackend(mediaItem.PhysicalFileName, mediaItem.Extension, mediaItem.Name);

                const processPromise: Promise<IExifResult> = Promise.resolve({
                    fileName: mediaItem.Name + mediaItem.Extension,
                    exifData: mediaItem.FileCreatedOn ? { DateTime: new Date(mediaItem.FileCreatedOn) } : undefined,
                });

                exifPromises.push(processPromise);
            }
        });

        return this.fiLoading.trackPromise(this.checkCreationDateInternal(exifPromises, deltaMs, skipErrorDialog));
    }
    //#region common exif utils

    private checkCreationDateInternal(exifPromises: Promise<IExifResult>[], deltaMs: number, skipErrorDialog: boolean) {
        return new Promise<boolean>((resolve, reject) => {
            Promise.all(exifPromises).then((results) => {
                const now = new Date();
                const noExifFileNames: string[] = [];
                const tooOldFileNames: string[] = [];
                results.forEach((r) => {
                    const fileDate = r.exifData && r.exifData.DateTime;
                    if (!fileDate) {
                        noExifFileNames.push(r.fileName);
                    } else if (now.getTime() - fileDate.getTime() > deltaMs) {
                        tooOldFileNames.push(r.fileName);
                    }
                });

                if (!!noExifFileNames.length || !!tooOldFileNames.length) {
                    if (!skipErrorDialog) {
                        const attachmentNames = noExifFileNames.concat(tooOldFileNames);
                        const attachmentNamesStr = attachmentNames.join(', ');

                        this.showAttachmentErrorDialog(
                            //file.attachment_is_not_valid_error
                            this.appTextService.get('file.attachment_is_not_valid_error'),
                            this.appTextService.get('file.attachment_is_not_valid_description', {
                                '{0}': attachmentNamesStr,
                            }),
                        );
                    }
                    resolve(false);
                }
                resolve(true);
            });
        });
    }

    private showAttachmentErrorDialog(headerText: string, messageText: string) {
        this.fiDialogsService.confirm({
            colorClass: 'red',
            isTopCloseButtonShown: true,
            headerIconClass: 'far fa-circle-exclamation red',
            headerText,
            messageText,
            isNoButtonHidden: true,
            yesButtonText: this.appTextService.get('ok'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                maxWidth: '640px',
            },
        });
    }
}
